import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { actualizarRegistro } from "../../../functions/crud";
import { crearRegistro } from "../../../functions/crud";
import { obtenerCenso } from "../../../functions/consultas";


export const useImportacionDatos = (datos, capturarDatos) => {
  const importarDatos = async () => {
    try {
      const { value: tipo } = await Swal.fire({
        title: "¿Qué deseas importar?",
        input: "select",
        inputOptions: {
          viviendas: "Viviendas",
          residentes: "Residentes",
          vehiculos: "Vehículos",
          mascotas: "Mascotas",
          autorizados: "Autorizados",
          contactos: "Contactos de Emergencia",
          espacios: "Espacios (Parqueaderos/Cuartos Útiles)",
        },
        inputPlaceholder: "Selecciona una opción",
        showCancelButton: true,
        confirmButtonText: "Continuar",
        cancelButtonText: "Cancelar",
      });

      if (!tipo) return;

      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".xlsx,.xls";

      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
          const data = await file.arrayBuffer();
          const workbook = XLSX.read(data, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet);

          const nit = localStorage.getItem("CodigoCliente");
          const total = rows.length;

          Swal.fire({
            title: "Importando...",
            html: `Procesando <b>0</b> de <b>${total}</b> registros`,
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          const updateProgress = (c) => {
            Swal.update({
              html: `Procesando <b>${c}</b> de <b>${total}</b> registros`,
            });
          };

          if (tipo === "viviendas") {
            await procesarViviendas(rows, nit, total, updateProgress);
          } else {
            await procesarDatosVivienda(tipo, rows, datos, nit, total, updateProgress);
          }

          Swal.fire("Éxito", `Se importaron ${tipo} correctamente`, "success");
          await capturarDatos();
        } catch (error) {
          console.error(error);
          Swal.fire("Error", "No se pudo importar el archivo", "error");
        }
      };

      input.click();
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Hubo un problema al importar los datos", "error");
    }
  };

  return importarDatos;
};

const procesarViviendas = async (rows, nit, total, updateProgress) => {
  let current = 0;
  for (const row of rows) {
    const registro = {
      fecha: new Date().toISOString(),
      usuario: localStorage.getItem("CorreoElectronico"),
      direccion: row.DESCRIPCION || row.DIRECCION,
      coeficiente: parseFloat(
        (row.COEFICIENTE || "0").toString().replace(",", ".")
      ),
    };
    await crearRegistro(`coordi_ph/${nit}/censo/`, registro);
    current++;
    updateProgress(current);
  }
};


const procesarDatosVivienda = async (tipo, rows, datos, nit, total, updateProgress) => {
  const filasAgrupadas = {};

  for (const row of rows) {
    const direccion = row.VIVIENDA || row.DIRECCION || row.DIRECCION_PRINCIPAL;
    if (!direccion) continue;

    if (!filasAgrupadas[direccion]) {
      filasAgrupadas[direccion] = [];
    }
    filasAgrupadas[direccion].push(row);
  }

  let current = 0;
  for (const [direccion, filas] of Object.entries(filasAgrupadas)) {
    const vivienda = datos.find(d => d.direccion === direccion);
    
    if (!vivienda) {
      console.warn(`Vivienda no encontrada: ${direccion}`);
      current += filas.length;
      updateProgress(current);
      continue;
    }

    const ruta = `coordi_ph/${nit}/censo/${vivienda.id}`;
    for (const row of filas) {
      const datosActualizados = await obtenerCenso();
      let viviendaActualizada = null;
      for (const id in datosActualizados) {
        if (datosActualizados[id].direccion === direccion) {
          viviendaActualizada = datosActualizados[id];
          break;
        }
      }

      await procesarTipoDato(tipo, row, viviendaActualizada, ruta);
      
      current++;
      updateProgress(current);
    }
  }
};


const obtenerSiguienteNumero = (vivienda, campoBase) => {
  if (!vivienda) return 1;
  const indices = [];
  for (const key in vivienda) {
    if (key.startsWith(campoBase + '_')) {
      const numeroStr = key.substring((campoBase + '_').length);
      const numero = parseInt(numeroStr);
      if (!isNaN(numero)) {
        indices.push(numero);
      }
    }
  }
  return indices.length > 0 ? Math.max(...indices) + 1 : 1;
};


const procesarTipoDato = async (tipo, row, vivienda, ruta) => {
  switch (tipo) {
    case "residentes":
      const siguienteResidente = obtenerSiguienteNumero(vivienda, 'nombre_residente');
      await actualizarRegistro(`${ruta}/nombre_residente_${siguienteResidente}`, row.Nombre_Completo || "");
      await actualizarRegistro(`${ruta}/cedula_residente_${siguienteResidente}`, row.Numero_Documento || "");
      await actualizarRegistro(`${ruta}/telefono_residente_${siguienteResidente}`, row.Telefono_Celular || "");
      await actualizarRegistro(`${ruta}/tipo_residente_${siguienteResidente}`, row.Tipo || "RESIDENTE");
      await actualizarRegistro(`${ruta}/correo_residente_${siguienteResidente}`, row.Correo_Electronico || "");
      await actualizarRegistro(`${ruta}/genero_residente_${siguienteResidente}`, row.Genero || "NO_ESPECIFICA");
      break;

    case "vehiculos":
      const siguienteVehiculo = obtenerSiguienteNumero(vivienda, 'placa');
      await actualizarRegistro(`${ruta}/placa_${siguienteVehiculo}`, (row.Placa || "").toUpperCase());
      await actualizarRegistro(`${ruta}/marca_carro_${siguienteVehiculo}`, row.Marca || "");
      await actualizarRegistro(`${ruta}/color_carro_${siguienteVehiculo}`, row.Color || "");
      await actualizarRegistro(`${ruta}/tipo_vehiculo_${siguienteVehiculo}`, row.Tipo_Vehiculo || "");
      await actualizarRegistro(`${ruta}/parqueadero_${siguienteVehiculo}`, row.Parqueadero || "");
      break;

    case "mascotas":
      const siguienteMascota = obtenerSiguienteNumero(vivienda, 'nombre_mascota');
      await actualizarRegistro(`${ruta}/nombre_mascota_${siguienteMascota}`, row.Nombre_Mascota || "");
      await actualizarRegistro(`${ruta}/tipo_mascota_${siguienteMascota}`, row.Tipo_Mascota || "PERRO");
      await actualizarRegistro(`${ruta}/raza_mascota_${siguienteMascota}`, row.Raza || "");
      await actualizarRegistro(`${ruta}/color_mascota_${siguienteMascota}`, row.Color_Pelaje || "");
      break;

    case "autorizados":
      const siguienteAutorizado = obtenerSiguienteNumero(vivienda, 'nombre_autorizado');
      await actualizarRegistro(`${ruta}/nombre_autorizado_${siguienteAutorizado}`, row.NOMBRE || "");
      await actualizarRegistro(`${ruta}/cedula_autorizado_${siguienteAutorizado}`, row.CEDULA || row.DOCUMENTO || "");
      await actualizarRegistro(`${ruta}/tipo_relacion_autorizado_${siguienteAutorizado}`, row.RELACION || row.TIPO_RELACION || "FAMILIAR");
      break;

    case "contactos":
      const siguienteContacto = obtenerSiguienteNumero(vivienda, 'nombre_contacto_emergencia');
      await actualizarRegistro(`${ruta}/nombre_contacto_emergencia_${siguienteContacto}`, row.NOMBRE || "");
      await actualizarRegistro(`${ruta}/telefono_contacto_emergencia_${siguienteContacto}`, row.TELEFONO || "");
      await actualizarRegistro(`${ruta}/descripcion_contacto_emergencia_${siguienteContacto}`, row.DESCRIPCION || row.PARENTESCO || "");
      break;

    case "espacios":
      const tipoEspacio = (row.TIPO_ADICIONAL || "").toUpperCase();
      const siguienteEspacio = obtenerSiguienteNumero(vivienda, 'espacio_tipo');
      
      await actualizarRegistro(`${ruta}/espacio_tipo_${siguienteEspacio}`, tipoEspacio);
      await actualizarRegistro(`${ruta}/espacio_identificador_${siguienteEspacio}`, row.IDENTIFICADOR || "");
      await actualizarRegistro(`${ruta}/espacio_coeficiente_${siguienteEspacio}`, parseFloat((row.COEFICIENTE || "0").toString().replace(",", ".")));
      break;

    default:
      break;
  }
};